'use strict';
import { Injectable, NgZone } from '@angular/core';
import { EXPORT_DATA_STORAGE_KEY } from '@ispm/shared/services/ui-customization.service';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { BehaviorSubject } from 'rxjs-compat/BehaviorSubject';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { BULK_OPERATION_COMMAND, IBulkOperationExecuter, IBulkOperationItem, IBulkOperationPollingItem } from '../interfaces/bulk-operation.interface';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { BulkOperationPollingService } from './bulk-operation-polling.service';
import { BulkOperationReassignService } from './bulk-operation-reassign.service';

const ALL_FIELDS = 'allFields';
const CURRENT_FIELDS = 'currentFields';
const NOTIFIED_STATUS = 'NOTIFIED';

@Injectable()
export class BulkOperationService {
  constructor(private _authService: AuthenticationService,
              private _ispmHttpClient: IspmHttpClient,
              private _bulkOperationPollingService: BulkOperationPollingService,
              private _bulkOperationReassignService: BulkOperationReassignService,
              private _notificationMessageService: NotificationMessageService,
              private _ngZone: NgZone
  ) {}

  private _activeBulkOperations$ = new BehaviorSubject<Array<IBulkOperationPollingItem>>(null);
  private _destroyer$: Subject<void> = new Subject();
  private _pollingInterval: number = 30000;
  private _pollingCreated: boolean;
  
  get activeBulkOperations$(): BehaviorSubject<Array<IBulkOperationPollingItem>> {
    return this._activeBulkOperations$;
  }
  
  set pollingInterval(value: number) {
    this._pollingInterval = value;
  }

  public register(worklistTemplateId: string){
    //Called when worklist is loaded
    if (!this._pollingCreated){ //Polling should be started only once
      this._pollingCreated = true;
      this._ngZone.runOutsideAngular(() => { //without this automatoin test will stuck
        this._bulkOperationPollingService.initPolling(this._pollingInterval)
        .subscribe(data => {
          if (data){
            this._activeBulkOperations$.pipe(take(1)).subscribe((operations: Array<IBulkOperationPollingItem>) => {
              if (JSON.stringify(data) != JSON.stringify(operations)){
                this._activeBulkOperations$.next(data);
              }
            }); 
          }
          else{
            this._activeBulkOperations$.next(data);
          }
        })
      })
    }
    this._bulkOperationPollingService.startPolling(worklistTemplateId);
  }

  public unregister(){
    this._activeBulkOperations$.next(null);
    this._bulkOperationPollingService.stopPolling();
  }

  public executeBulkOperation(operationConfig: IBulkOperationItem): string {
    const maxItems: number = operationConfig.maxItems ? operationConfig.maxItems : 1000;
    let selectedTests: Array<any>;
    let excludedIds;
    let resourceIdentifier: string;

    let cachedDataStr = sessionStorage.getItem(EXPORT_DATA_STORAGE_KEY); //Using the same worklist selection details as in export feature
    let cachedData: any;
    if (!!cachedDataStr){
      cachedData = JSON.parse(cachedDataStr);
      let queryConfigNode: any;
      if (cachedData[ALL_FIELDS] && Array.isArray(cachedData[ALL_FIELDS].resources)){
        queryConfigNode = cachedData[ALL_FIELDS].resources[0];
      }
      else if (cachedData[CURRENT_FIELDS] && Array.isArray(cachedData[CURRENT_FIELDS].resources)){
        queryConfigNode = cachedData[CURRENT_FIELDS].resources[0];
      }
      else{
        this._notificationMessageService.error('Cannot find cached items');
        return;
      }
      selectedTests = queryConfigNode.combinedIds;
      excludedIds = queryConfigNode.excludedIds;
      resourceIdentifier = queryConfigNode.resourceIdentifier;
    }
    else{
      this._notificationMessageService.warning('No items were selected');
      return;
    }

    let exceededTestsAmount: boolean;
     //Take the first X items in case more than the limitation were selected
    if (!!cachedData.selectionCount && cachedData.selectionCount > maxItems){
      exceededTestsAmount = true; // Components will use this to notify the user that not all selected items will be executed
      selectedTests = selectedTests.slice(0, maxItems);
    }
    selectedTests = selectedTests.map(item => item.id);

    const executerService: IBulkOperationExecuter = this.getServiceByOperationType(operationConfig);
    if (!executerService){
      console.error('BulkOperationService->executeBulkOperation: bulk operation is not supported. Type = ' + operationConfig.type)
      return;
    }

    executerService.beforeExecute(operationConfig, selectedTests, exceededTestsAmount, maxItems)
    .take(1).subscribe((data) => {
			if (data && data.payload) {
        if (data.payload.action == 'cancel')
        {
          return;
        }
        const worklistTemplateId = cachedData['worklistTemplateId'];
        const userId = this._authService.user.info.id;
        const selectedTestsStr: string = selectedTests.join(','); //comma separated string with all diagnostic orders ids
        const selectedTestsQuery: string = cachedData['query']; //In case too many tests were selected, sending the query to the web
        if (!selectedTestsQuery){ //Do not send excludedIds in non-query mode
          excludedIds = null;
        }
        else if (selectedTestsQuery['params'] && selectedTestsQuery['params'].target){
          selectedTestsQuery['params'].target._count = maxItems; //The count should be taken from the bulk config (and not from the original query)
        }
        this.sendBulkOperationRequest(selectedTestsStr, selectedTestsQuery, excludedIds, data.payload, 
          worklistTemplateId, userId, resourceIdentifier, operationConfig);
			}
		});
	}

  private sendBulkOperationRequest(selectedTests, selectedTestsQuery, excludedIds, executionParameters, worklistTemplateId, userId,
                                     resourceIdentifier, operationConfig){
    let bulkOperationObj = {
      diagnosticOrderIds: selectedTests,
      selectedOrdersQuery: selectedTestsQuery,
      actorId: userId,
      worklistTemplateId: worklistTemplateId,
      excludedIds: excludedIds,
      resourceIdentifier: resourceIdentifier
    }
    if (!!operationConfig.additionalParameters){ //Adding parameters from configuration (e.g. etlFlowDefinition)
      bulkOperationObj = Object.assign(bulkOperationObj, operationConfig.additionalParameters);
    }

    if (!!executionParameters){ //Adding parameters that came from the executer service which are specific for the operation (e.g. note for reassign operation)
      bulkOperationObj = Object.assign(bulkOperationObj, executionParameters);
    }

    this._ispmHttpClient.post(operationConfig.URL, bulkOperationObj, true)
    .take(1).subscribe((bulkOperationRes: IBulkOperationPollingItem) => {
      this._activeBulkOperations$.next([bulkOperationRes]);
      this._bulkOperationPollingService.startPolling(worklistTemplateId);
    },
    error => {
      this._notificationMessageService.error('Bulk operation failed.');
      console.error('---> sendBulkOperationRequest ERROR', error);
    });
  }
    
  public setBulkOperationNotified(EtlFlowRequest: any) {
    //Update notified status after user message was shown
    const notificationStatusItem = EtlFlowRequest.context.find(item => item.resourceType === 'notificationStatus');
    if (notificationStatusItem){
      notificationStatusItem.reference = NOTIFIED_STATUS;
      if (EtlFlowRequest.status && EtlFlowRequest.status.value === 'PARTLY'){ //'PARTLY' is an internal status that should not be saved in the DB. The original status ('SUCCEEDED') should be saved.
        EtlFlowRequest.status.value = 'SUCCEEDED';
      }
      this._ispmHttpClient.put('entity-tree/EtlFlowRequest/'+ EtlFlowRequest.id, EtlFlowRequest)
      .take(1).subscribe(() => {})
    }
    else{
      console.error('setBulkOperationNotified: notificationStatus parameter is missing')
    }
  }

  getServiceByOperationType(operation: IBulkOperationItem): IBulkOperationExecuter {
    switch (operation.type) {
      case BULK_OPERATION_COMMAND.REASSIGN:
        return this._bulkOperationReassignService;
      default:
        console.warn('BulkOperationComponent->getServiceByOperationType: operation was not found: ' + operation.type)
        return null;
    }
  }  
}
